/**
 * A casca da aplicação: navegação, identidade e a faixa de sessão expirada.
 *
 * ── Por que o menu é este, e não o desenho das engines ────────────────────
 *
 * A §11 é a régua: *"o frontend deve ser desenhado por tarefas do usuário, não
 * como reprodução das engines"*. "Atendimento", "Funil" e "Tarefas" são o que o
 * vendedor faz. "Twenty" e "Chatwoot" não aparecem em lugar nenhum — nem aqui,
 * nem em mensagem de erro.
 *
 * ── O que está fora ───────────────────────────────────────────────────────
 *
 * As telas em si, que são H04 em diante. Os itens do menu existem porque a
 * casca é o que a H01 entrega, mas levam a uma área de espera honesta, que diz
 * qual task traz aquela tela. Link para tela inexistente que finge carregar é
 * pior do que link que diz "ainda não".
 */

import { useRef, useState, type ReactNode } from 'react';
import type { Me } from '@cliqcrm/contracts';
import {
  geraSuperAdmins,
  papelVeRelatorios,
  podeCriarOrganizacaoComo,
  VE_INTEGRACAO_DE_DADOS,
} from '@/entities/acesso/papel';
import { Atendimento } from '@/pages/atendimento/atendimento';
import { Sino, useAvisoDeMensagens } from '@/widgets/notificacoes';
import { PilhaDeNotificacoes } from '@/widgets/notificacoes';
import { ConviteDosAvisos } from '@/widgets/notificacoes';
import { DialogoDeMencoesParaMim, MencoesParaMimNoMenu } from '@/widgets/notificacoes';
import { papelRecebeNotificacao } from '@/widgets/notificacoes';
import { ClienteTrezentosESessenta } from '@/pages/clientes/cliente-360';
import { FILTRO_INICIAL, ListaDeClientes } from '@/pages/clientes/lista';
import type { FiltroDaLista } from '@/entities/clientes/cliente';
import { Funil } from '@/pages/comercial/funil';
import { TelaDaIa } from '@/pages/ia/apresentacao';
import { useTransicaoDeTela } from './usar-transicao-de-tela';
import {
  AreaDasAutomacoes,
  ConfiguracaoDoModelo,
  HistoricoDaAutomacao,
} from '@/pages/automacoes/area';
import { EditorDoMapa } from '@/pages/automacoes/editor';
import { Acompanhamento } from '@/pages/automacoes/acompanhamento';
import { Listas } from '@/pages/automacoes/listas';
import { Integracoes } from '@/pages/automacoes/integracoes';
import { Operacoes } from '@/pages/automacoes/operacoes';
import { AutomacoesEmBreve } from '@/pages/automacoes/em-breve';
import { Inbox } from '@/pages/atendimento/conversas';
import { Painel } from '@/pages/relatorios/relatorios';
import { RankingDeVendasTela } from '@/pages/relatorios/relatorios';
import { GargalosTela } from '@/pages/relatorios/relatorios';
import { ComparativosTela } from '@/pages/relatorios/relatorios';
import { AtendimentoTela } from '@/pages/relatorios/relatorios';
import { RevisaoDeIdentidade } from '@/pages/clientes/unificacao';
import { Administracao } from '@/pages/administracao/configuracoes';
import { Tarefas } from '@/pages/comercial/tarefas';
import { useSessao } from '@/shared/sessao/contexto';
import { useObservarRecurso } from '@/shared/api/assinaturas';
import { useApi, useOrganizacoesDaPessoa, useSegurancaDaConta } from '@/shared/api/consultas';
import { useAvisarAbaVisivel } from '@/widgets/notificacoes';
import { desligarPush } from '@/widgets/notificacoes';
import { useAberturaPeloAviso, useSincronizarPush } from '@/widgets/notificacoes';
import { adiarAviso, lerAdiamentoDoAviso } from '@/shared/sessao/aviso-de-segundo-fator';
import { sairDaTela } from '@/shared/lib/guarda-de-saida';
import './casca.css';
import { retornoDaMeta } from '@/pages/administracao/configuracoes';
import {
  caminhoDaRota,
  ROTA_INICIAL,
  rotaDoCaminho,
  useEnderecoDaTela,
  type PaginaDasAutomacoes,
  type Rota,
} from '@/app/rotas/rotas';
import { TelaDeFeedbacks } from '@/pages/operacao/feedbacks';
import { TelaDeSuperAdmins } from '@/pages/operacao/super-admins';
import { PaginaDePlanos } from '@/pages/operacao/planos';
import { NovaOrganizacao } from '@/pages/operacao/nova-organizacao';
import {
  FILTRO_INICIAL_DAS_ORGANIZACOES,
  PaginaDeOrganizacoes,
  type FiltroDasOrganizacoes,
} from '@/pages/operacao/organizacoes';
import { PaginaDaOrganizacao } from '@/pages/operacao/organizacao';
import { AreaDeAvisos } from '@/shared/ui';
import {
  BarraMovel,
  automacoesLiberadas,
  MenuLateral,
  nomeDaTelaParaFeedback,
  RELATORIOS,
  SECOES_DA_ADMINISTRACAO,
  secoesVisiveisPara,
  tituloDaBarra,
  useTrilho,
  type Area,
  type SecaoDaAdministracao,
  type SecaoDosRelatorios,
} from '@/widgets/menu-lateral';

/**
 * O aviso de conta sem segundo fator, no topo, está DESLIGADO (23/10/2026).
 *
 * Saiu a pedido: por ora o segundo fator não é cobrado de ninguém, e a faixa
 * aparecendo em toda tela só atrapalha. O código fica para quando voltar a
 * valer — religar é trocar para `true`. A seção "Segurança" das Configurações
 * continua mostrando e cadastrando o segundo fator.
 */
const AVISO_DE_SEGUNDO_FATOR_LIGADO: boolean = false;

/**
 * A rota que a pessoa pode abrir (#806).
 *
 * O endereço pode pedir qualquer tela; aqui ele é conferido contra o papel,
 * com as mesmas regras que decidem os itens do menu. Tela que a pessoa não vê
 * vira o Atendimento, sem dizer que a tela existe. Isto é só a porta da TELA:
 * os dados continuam protegidos pela API (token, escopo e RLS), que recusa do
 * mesmo jeito quem chegar por outro caminho.
 *
 * Sem `me` (sessão expirada, reentrando), nada é corrigido: a tela fica onde
 * estava até a sessão voltar.
 */
export function rotaPermitida(rota: Rota, me: Me | null): Rota {
  if (!me) return rota;
  switch (rota.tela) {
    case 'feedbacks':
      return me.suporte ? rota : ROTA_INICIAL;
    case 'organizacoes':
    case 'organizacao':
    case 'nova-organizacao':
    case 'planos':
      return podeCriarOrganizacaoComo(me) ? rota : ROTA_INICIAL;
    case 'super-admins':
      return geraSuperAdmins(me.papel) ? rota : ROTA_INICIAL;
    case 'painel':
      return papelVeRelatorios(me.papel) && RELATORIOS.some((r) => r.chave === rota.secao)
        ? rota
        : ROTA_INICIAL;
    case 'automacoes':
      // Sem a funcionalidade, a área é a tela de "Em breve", e ela mora em /automacoes.
      if (!automacoesLiberadas(me) && rota.pagina.tipo !== 'lista') {
        return { tela: 'automacoes', pagina: { tipo: 'lista' } };
      }
      // As integrações são só da Cliqfy: para os outros, a tela não existe.
      if (
        (rota.pagina.tipo === 'integracoes' || rota.pagina.tipo === 'operacoes') &&
        !VE_INTEGRACAO_DE_DADOS.has(me.papel)
      ) {
        return ROTA_INICIAL;
      }
      return rota;
    case 'configuracoes': {
      const secao = SECOES_DA_ADMINISTRACAO.find((s) => s.chave === rota.secao);
      const ve =
        secao !== undefined &&
        (secao.foraDoMenu === true ||
          secoesVisiveisPara(me.papel, me.flags).some((s) => s.chave === rota.secao));
      return ve ? rota : ROTA_INICIAL;
    }
    default:
      return rota;
  }
}

/** A rota com que a casca abre: a volta da Meta, ou o endereço da página. */
function rotaDaAbertura(me: Me | null): Rota {
  if (retornoDaMeta()) return { tela: 'configuracoes', secao: 'conexoes' };
  return rotaPermitida(rotaDoCaminho(window.location.pathname) ?? ROTA_INICIAL, me);
}

export function Casca(): ReactNode {
  const { estado, reentrar, sair, trocarDeRevenda } = useSessao();
  /*
   * Em toda tela: uma funcionalidade ligada, o papel ou o acesso ao prontuário
   * mudam o `/v1/me`, e a tela o relê quando o aviso chega (21/10/2026). Antes
   * a mudança só aparecia depois de recarregar a página.
   */
  useObservarRecurso(estado.nome === 'autenticado' ? 'sessao:eu' : null);
  /*
   * Só aparece para quem TEM para onde trocar.
   *
   * Mostrar sempre e descobrir no clique levaria quem tem uma organização só a
   * uma ida ao servidor que termina exatamente onde começou.
   */
  const revendas = useOrganizacoesDaPessoa();
  const podeTrocarDeRevenda = (revendas.data?.itens.length ?? 0) > 1;

  /*
   * A tela com que a casca abre sai do ENDEREÇO (#806): recarregar mantém a
   * tela, e um link leva direto a ela. Lida uma vez, na montagem.
   */
  const [abertura] = useState<Rota>(() =>
    rotaDaAbertura(estado.nome === 'autenticado' ? estado.me : null),
  );
  // A tela de atendimento é a primeira porque é o que a pessoa abre para
  // trabalhar. "Início" existe só enquanto o mostruário dos estados tem valor.
  const [area, definirArea] = useState<Area>(() => abertura.tela);

  /** A seção dos relatórios, pelo mesmo motivo da seção da administração: quem a desenha é o menu. */
  const [secaoDosRelatorios, definirSecaoDosRelatorios] = useState<SecaoDosRelatorios>(() =>
    abertura.tela === 'painel' ? (abertura.secao as SecaoDosRelatorios) : 'visao-geral',
  );

  /**
   * Em qual seção da administração a pessoa está.
   *
   * Mora aqui, e não na tela, porque quem a DESENHA é o menu lateral. Deixá-la
   * lá dentro faria o trilho acender "Configurações" sem saber qual dos quatro
   * subitens está aberto — e o menu perderia justamente a informação que ele
   * existe para dar.
   */
  const [secaoDaAdmin, definirSecaoDaAdmin] = useState<SecaoDaAdministracao>(() =>
    abertura.tela === 'configuracoes' ? (abertura.secao as SecaoDaAdministracao) : 'configuracao',
  );

  /** Qual tela das automações está aberta: a lista, a galeria de modelos. */
  const [paginaDasAutomacoes, definirPaginaDasAutomacoes] = useState<PaginaDasAutomacoes>(() =>
    abertura.tela === 'automacoes' ? abertura.pagina : { tipo: 'lista' },
  );

  /*
   * O aviso do segundo fator: a VERDADE vem do provedor, não do token.
   *
   * `comSegundoFator` da sessão lê o `amr`, que diz como a pessoa entrou nesta
   * sessão — quem cadastra com a sessão aberta continua com `amr: ['pwd']` e
   * veria o aviso para sempre. Aconteceu: a pessoa cadastrou duas vezes,
   * achando que não tinha pegado.
   *
   * `null` do servidor é "não foi possível saber" (provedor fora do ar, ou conta
   * sem par no provedor), e não vira aviso: um empurrão a menos é melhor que um
   * aviso falso.
   */
  const seguranca = useSegurancaDaConta();
  const [avisoAdiadoAte, definirAvisoAdiadoAte] = useState(() => lerAdiamentoDoAviso());

  const precisaAvisarDoSegundoFator =
    AVISO_DE_SEGUNDO_FATOR_LIGADO &&
    seguranca.data?.segundoFator === false &&
    Date.now() >= avisoAdiadoAte;

  const adiarAvisoDeSegundoFator = (): void => {
    definirAvisoAdiadoAte(adiarAviso());
  };

  /*
   * O aviso de mensagens é lido AQUI, e não dentro do sino.
   *
   * Com o trilho recolhido o sino não é desenhado — o aviso vira a bolinha no
   * ícone de Atendimento —, e se a busca morasse nele o som pararia de tocar
   * justamente para quem trabalha com o menu fechado.
   */
  const aviso = useAvisoDeMensagens(
    papelRecebeNotificacao(estado.nome === 'autenticado' ? estado.me.papel : null),
  );

  const [vendoMencoes, definirVendoMencoes] = useState(false);
  /**
   * O canal escolhido no painel, entregue à Inbox.
   *
   * Mora aqui pela mesma razão da conversa e do cliente: o detalhamento do
   * painel troca de tela E leva um filtro junto. Guardado dentro da Inbox, o
   * clique no painel abriria a fila sem filtro nenhum.
   */
  const [canalDoPainel, definirCanalDoPainel] = useState<string | null>(null);

  /**
   * A conversa escolhida na fila, entregue ao Atendimento.
   *
   * Guardada aqui, e não dentro de cada tela: abrir da fila precisa levar ao
   * atendimento COM a conversa selecionada, e as duas telas são irmãs. Um
   * estado por tela faria o clique na fila trocar de tela e abrir vazio.
   */
  const [conversaEscolhida, definirConversaEscolhida] = useState<string | null>(() =>
    abertura.tela === 'atendimento' ? abertura.conversa : null,
  );

  /**
   * O cliente aberto no 360.
   *
   * Mora aqui pela mesma razão da conversa: abrir o 360 vem de dentro do
   * atendimento, e voltar dele precisa devolver à tela de onde se veio. Guardar
   * dentro de cada tela faria a volta perder o contexto.
   */
  const [clienteAberto, definirClienteAberto] = useState<string | null>(() =>
    abertura.tela === 'cliente' ? abertura.id : null,
  );
  // Aberto por um link, o 360 volta para a lista de clientes.
  const [voltarPara, definirVoltarPara] = useState<
    'atendimento' | 'inbox' | 'clientes' | 'funil' | 'tarefas'
  >(() => (abertura.tela === 'cliente' ? 'clientes' : 'atendimento'));

  /*
   * O recorte da lista de clientes vive AQUI, e não dentro da tela.
   *
   * É a Armadilha da H17: *"perder a busca ao voltar da ficha"*. Abrir o 360
   * desmonta a lista, e um estado interno morreria com ela — refazer a busca a
   * cada consulta é o que faz alguém abrir a ficha em outra aba e nunca mais
   * usar a lista.
   *
   * Os quatro campos juntos e não só a busca: voltar da ficha para "Todos"
   * ordenado por data desfaria uma triagem que a pessoa acabou de montar, e a
   * perda seria maior que a do termo.
   */
  const [filtroDeClientes, definirFiltroDeClientes] = useState<FiltroDaLista>(FILTRO_INICIAL);

  /*
   * A organização aberta na gestão, e a busca e o filtro da lista, pelo mesmo
   * motivo do recorte de clientes: abrir uma organização desmonta a lista, e
   * voltar dela não pode perder o que a pessoa digitou (#767).
   */
  const [organizacaoAberta, definirOrganizacaoAberta] = useState<string | null>(() =>
    abertura.tela === 'organizacao' ? abertura.id : null,
  );
  const [filtroDasOrganizacoes, definirFiltroDasOrganizacoes] = useState<FiltroDasOrganizacoes>(
    FILTRO_INICIAL_DAS_ORGANIZACOES,
  );

  const abrirCliente = (
    id: string,
    de: 'atendimento' | 'inbox' | 'clientes' | 'funil' | 'tarefas',
  ) => {
    definirClienteAberto(id);
    definirVoltarPara(de);
    definirArea('cliente');
  };

  const abrirConversa = (id: string) => {
    definirConversaEscolhida(id);
    definirArea('atendimento');
  };

  /*
   * A URL e a tela andam juntas (#806): a rota sai dos estados acima, e o
   * "voltar" do navegador aplica a rota de volta neles — passando pela mesma
   * conferência de permissão da abertura.
   */
  const rotaAtual: Rota =
    area === 'cliente' && clienteAberto
      ? { tela: 'cliente', id: clienteAberto }
      : area === 'organizacao' && organizacaoAberta
        ? { tela: 'organizacao', id: organizacaoAberta }
        : area === 'organizacao'
          ? { tela: 'organizacoes' }
          : area === 'painel'
            ? { tela: 'painel', secao: secaoDosRelatorios }
            : area === 'configuracoes'
              ? { tela: 'configuracoes', secao: secaoDaAdmin }
              : area === 'automacoes'
                ? { tela: 'automacoes', pagina: paginaDasAutomacoes }
                : area === 'atendimento' || area === 'inicio' || area === 'cliente'
                  ? { tela: 'atendimento', conversa: conversaEscolhida }
                  : { tela: area };

  useEnderecoDaTela(rotaAtual, (lida) => {
    const rota = rotaPermitida(lida, estado.nome === 'autenticado' ? estado.me : null);
    if (rota.tela === 'cliente') definirClienteAberto(rota.id);
    if (rota.tela === 'organizacao') definirOrganizacaoAberta(rota.id);
    if (rota.tela === 'painel') definirSecaoDosRelatorios(rota.secao as SecaoDosRelatorios);
    if (rota.tela === 'configuracoes') definirSecaoDaAdmin(rota.secao as SecaoDaAdministracao);
    if (rota.tela === 'atendimento') definirConversaEscolhida(rota.conversa);
    if (rota.tela === 'automacoes') definirPaginaDasAutomacoes(rota.pagina);
    definirArea(rota.tela);
    return rota;
  });

  // O push com o CRM fechado (#536): a inscrição em dia ao entrar, e o clique
  // no aviso abrindo a conversa — ver `notificacoes/usar-push.ts`.
  const api = useApi();
  useSincronizarPush(api);
  // Com a aba à vista, o push não vira aviso do sistema — ver `aba-visivel.ts`.
  useAvisarAbaVisivel();
  useAberturaPeloAviso(estado.nome === 'autenticado' ? estado.revenda : null, abrirConversa);

  /*
   * Sair também tira o push DESTE navegador: no computador do balcão, quem
   * entra depois não pode receber o aviso de quem saiu. Best-effort — sair
   * nunca espera o servidor de push.
   */
  const sairDeVez = async (): Promise<void> => {
    await desligarPush(api).catch(() => undefined);
    await sair();
  };

  /*
   * A tela nova entra pela opacidade (150ms). A chave é a tela e a seção do
   * submenu: trocar de conversa no Atendimento não anima.
   */
  const palco = useRef<HTMLDivElement>(null);
  useTransicaoDeTela(
    palco,
    area === 'configuracoes'
      ? `configuracoes:${secaoDaAdmin}`
      : area === 'painel'
        ? `painel:${secaoDosRelatorios}`
        : area === 'automacoes'
          ? `automacoes:${paginaDasAutomacoes.tipo}`
          : area,
  );

  const estadoDoTrilho = useTrilho(area, secaoDaAdmin, {
    // O editor do mapa precisa da largura: o menu fica só com os ícones.
    forcarRecolhido: area === 'automacoes' && paginaDasAutomacoes.tipo === 'editor',
  });

  if (estado.nome !== 'autenticado' && estado.nome !== 'expirado') return null;

  const expirado = estado.nome === 'expirado';
  const me = estado.nome === 'autenticado' ? estado.me : null;
  // Só o Super Admin cria, que é o que a API aceita (emenda de 21/10 ao ADR-0025).
  const podeCriarOrganizacao = podeCriarOrganizacaoComo(me);

  /*
   * O submenu de Configurações depende do papel: "Conexões" é integração, e
   * integração é do administrador da organização (§13.2). Sem este recorte, um
   * gestor via o item, clicava e recebia a recusa da API — uma porta que existe
   * só para dizer não.
   */
  const secoesDestaPessoa = secoesVisiveisPara(me?.papel, me?.flags);

  const { recolhidoDeFato, espiando, aberta } = estadoDoTrilho;

  return (
    <div
      className="casca"
      data-recolhido={recolhidoDeFato ? 'sim' : undefined}
      data-espiando={recolhidoDeFato && espiando ? 'sim' : undefined}
      data-gaveta={aberta ? 'aberta' : undefined}
    >
      <a className="pular" href="#conteudo">
        Pular para o conteúdo
      </a>

      <MenuLateral
        trilho={estadoDoTrilho}
        me={me}
        area={area}
        aoAbrirArea={(a) =>
          sairDaTela(() => {
            // O item do menu leva sempre à lista, mesmo de dentro da galeria.
            if (a === 'automacoes') definirPaginaDasAutomacoes({ tipo: 'lista' });
            definirArea(a);
          })
        }
        aoAbrirPainel={() =>
          sairDaTela(() => {
            definirCanalDoPainel(null);
            definirArea('painel');
          })
        }
        secaoDosRelatorios={secaoDosRelatorios}
        aoEscolherRelatorio={(secao) => sairDaTela(() => definirSecaoDosRelatorios(secao))}
        secaoDaAdmin={secaoDaAdmin}
        secaoDasAutomacoes={
          area !== 'automacoes'
            ? null
            : paginaDasAutomacoes.tipo === 'acompanhamento'
              ? 'acompanhamento'
              : paginaDasAutomacoes.tipo === 'listas'
                ? 'listas'
                : paginaDasAutomacoes.tipo === 'integracoes'
                  ? 'integracoes'
                  : paginaDasAutomacoes.tipo === 'operacoes'
                    ? 'operacoes'
                    : 'lista'
        }
        aoEscolherSecaoDasAutomacoes={(secao) =>
          sairDaTela(() => {
            // O submenu agora aparece fora da área: escolher nele também leva até ela.
            definirArea('automacoes');
            definirPaginaDasAutomacoes(
              secao === 'acompanhamento'
                ? { tipo: 'acompanhamento', falhou: false, automacao: null }
                : secao === 'listas'
                  ? { tipo: 'listas', lista: null }
                  : secao === 'integracoes'
                    ? { tipo: 'integracoes', nova: false }
                    : secao === 'operacoes'
                      ? { tipo: 'operacoes', id: null, nova: false }
                      : { tipo: 'lista' },
            );
          })
        }
        aoEscolherSecaoDaAdmin={(secao) => sairDaTela(() => definirSecaoDaAdmin(secao))}
        secoesDestaPessoa={secoesDestaPessoa}
        aviso={aviso}
        avisos={
          <>
            <Sino mensagens={aviso.mensagens} conversas={aviso.conversas} />
            {papelRecebeNotificacao(me?.papel) && (
              <MencoesParaMimNoMenu aoAbrir={() => definirVendoMencoes(true)} />
            )}
          </>
        }
        podeTrocarDeRevenda={podeTrocarDeRevenda}
        aoTrocarDeRevenda={() => sairDaTela(() => trocarDeRevenda())}
        aoSair={() => sairDaTela(() => void sairDeVez())}
        telaDoFeedback={{
          caminho: caminhoDaRota(rotaAtual),
          nome: nomeDaTelaParaFeedback(area, secaoDaAdmin, secoesDestaPessoa),
        }}
      />

      <div className="casca__palco" ref={palco}>
        <BarraMovel
          trilho={estadoDoTrilho}
          titulo={tituloDaBarra(area, secaoDaAdmin, secoesDestaPessoa)}
        />
        {expirado && (
          <div className="casca__faixa casca__faixa--expirada" role="alert">
            <span>
              <b>Sua sessão expirou.</b> O que você estava escrevendo continua aqui — entre de novo
              para poder salvar.
            </span>
            <button className="casca__acao" type="button" onClick={() => void reentrar()}>
              Entrar de novo
            </button>
          </div>
        )}

        {me && precisaAvisarDoSegundoFator && (
          <div className="casca__faixa casca__faixa--aviso">
            <span>
              Sua conta ainda não tem segundo fator. Ele protege o acesso mesmo que a senha vaze.
            </span>
            <button
              className="casca__acao"
              type="button"
              onClick={() => {
                definirSecaoDaAdmin('seguranca');
                definirArea('configuracoes');
              }}
            >
              Ver em Configurações
            </button>
            {/*
              Dispensar é o que impede o aviso de virar paisagem.

              Ele é um empurrão, não um bloqueio: quem está no meio de um
              atendimento e não pode parar para cadastrar precisa de uma saída
              que não seja aprender a ignorá-lo. Some por uma semana e volta —
              e some de vez assim que o segundo fator existir, porque aí a
              resposta do provedor muda e a condição deixa de valer.
            */}
            <button
              className="casca__acao casca__acao--discreta"
              type="button"
              onClick={adiarAvisoDeSegundoFator}
            >
              Agora não
            </button>
          </div>
        )}

        {/*
          O convite para ligar os avisos do navegador (#674): a permissão é
          por endereço e por navegador, e quem não abre o controle do
          atendimento nunca descobriria que precisa ligar.
        */}
        {me && (
          <ConviteDosAvisos
            usuarioId={me.usuario.id}
            recebeNotificacao={papelRecebeNotificacao(me.papel)}
          />
        )}

        {area === 'inbox' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <Inbox
              aoAbrir={abrirConversa}
              canalInicial={canalDoPainel}
              /*
               * O canal é intenção de UMA navegação, e some depois de aplicado.
               *
               * Sem isto ele voltava toda vez que a fila era aberta pelo menu:
               * a pessoa limpava o filtro, ia para outra tela, voltava — e a
               * fila estava filtrada de novo, por um filtro que ela não tinha
               * escolhido. Parecia fila quebrada, e era memória nossa.
               */
              aoConsumirCanal={() => definirCanalDoPainel(null)}
            />
          </main>
        ) : area === 'funil' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <Funil
              aoAbrirConversa={abrirConversa}
              aoAbrirCliente={(id) => abrirCliente(id, 'funil')}
              aoConfigurarEtapas={() => definirArea('etapas-do-funil')}
            />
          </main>
        ) : area === 'tarefas' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <Tarefas
              aoAbrirConversa={abrirConversa}
              aoAbrirCliente={(id) => abrirCliente(id, 'tarefas')}
            />
          </main>
        ) : area === 'ia' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <TelaDaIa />
          </main>
        ) : area === 'automacoes' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            {!automacoesLiberadas(me) ? (
              <AutomacoesEmBreve />
            ) : paginaDasAutomacoes.tipo === 'integracoes' ? (
              // A guarda da rota não age enquanto `me` carrega: confere aqui também.
              me && VE_INTEGRACAO_DE_DADOS.has(me.papel) ? (
                <Integracoes
                  nova={paginaDasAutomacoes.nova}
                  aoFecharNova={() =>
                    definirPaginaDasAutomacoes({ tipo: 'integracoes', nova: false })
                  }
                />
              ) : null
            ) : paginaDasAutomacoes.tipo === 'listas' ? (
              <Listas
                lista={paginaDasAutomacoes.lista}
                aoEscolher={(lista) => definirPaginaDasAutomacoes({ tipo: 'listas', lista })}
                {...(me && VE_INTEGRACAO_DE_DADOS.has(me.papel)
                  ? {
                      aoIrParaOperacoes: () =>
                        definirPaginaDasAutomacoes({ tipo: 'operacoes', id: null, nova: true }),
                    }
                  : {})}
              />
            ) : paginaDasAutomacoes.tipo === 'operacoes' ? (
              me && VE_INTEGRACAO_DE_DADOS.has(me.papel) ? (
                <Operacoes
                  id={paginaDasAutomacoes.id}
                  nova={paginaDasAutomacoes.nova}
                  aoAbrir={(id) =>
                    definirPaginaDasAutomacoes({ tipo: 'operacoes', id, nova: false })
                  }
                  aoNova={() =>
                    definirPaginaDasAutomacoes({ tipo: 'operacoes', id: null, nova: true })
                  }
                  aoVoltarParaALista={() =>
                    definirPaginaDasAutomacoes({ tipo: 'operacoes', id: null, nova: false })
                  }
                  aoIrParaIntegracoes={() =>
                    definirPaginaDasAutomacoes({ tipo: 'integracoes', nova: false })
                  }
                />
              ) : null
            ) : paginaDasAutomacoes.tipo === 'acompanhamento' ? (
              <Acompanhamento
                key={`${paginaDasAutomacoes.automacao ?? ''}:${paginaDasAutomacoes.falhou}`}
                falhou={paginaDasAutomacoes.falhou}
                automacao={paginaDasAutomacoes.automacao}
                aoAbrirMapa={(id, versao) =>
                  definirPaginaDasAutomacoes({ tipo: 'editor', id, versao })
                }
              />
            ) : paginaDasAutomacoes.tipo === 'editor' ? (
              <EditorDoMapa
                key={`${paginaDasAutomacoes.id}:${paginaDasAutomacoes.versao ?? 'agora'}`}
                id={paginaDasAutomacoes.id}
                versao={paginaDasAutomacoes.versao}
                recemCriado={paginaDasAutomacoes.novo === true}
                aoVoltar={() => definirPaginaDasAutomacoes({ tipo: 'lista' })}
                modelo={(automacao) => <ConfiguracaoDoModelo automacao={automacao} />}
                historico={(automacao, nome, aoFechar) => (
                  <HistoricoDaAutomacao
                    automacao={automacao}
                    nome={nome}
                    papel={estado.nome === 'autenticado' ? estado.me.papel : null}
                    meuId={estado.nome === 'autenticado' ? estado.me.usuario.id : null}
                    aoFechar={aoFechar}
                    aoVerVersao={(v) =>
                      sairDaTela(() =>
                        definirPaginaDasAutomacoes({
                          tipo: 'editor',
                          id: paginaDasAutomacoes.id,
                          versao: v,
                        }),
                      )
                    }
                  />
                )}
                aoVerAtual={() =>
                  definirPaginaDasAutomacoes({
                    tipo: 'editor',
                    id: paginaDasAutomacoes.id,
                    versao: null,
                  })
                }
              />
            ) : (
              <AreaDasAutomacoes
                pagina={paginaDasAutomacoes.tipo}
                aoNavegar={(tipo) => definirPaginaDasAutomacoes({ tipo })}
                aoAbrirMapa={(id, versao, novo) =>
                  definirPaginaDasAutomacoes({
                    tipo: 'editor',
                    id,
                    versao,
                    ...(novo ? { novo: true } : {}),
                  })
                }
                aoVerExecucoes={(automacao, falhou) =>
                  definirPaginaDasAutomacoes({
                    tipo: 'acompanhamento',
                    falhou,
                    automacao,
                  })
                }
              />
            )}
          </main>
        ) : area === 'painel' && secaoDosRelatorios !== 'visao-geral' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            {secaoDosRelatorios === 'ranking' ? (
              <RankingDeVendasTela />
            ) : secaoDosRelatorios === 'gargalos' ? (
              <GargalosTela />
            ) : secaoDosRelatorios === 'comparativos' ? (
              <ComparativosTela />
            ) : (
              <AtendimentoTela />
            )}
          </main>
        ) : area === 'painel' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <Painel
              aoAbrirCanal={(canal) => {
                definirCanalDoPainel(canal);
                definirArea('inbox');
              }}
              aoAbrirFunil={() => definirArea('funil')}
            />
          </main>
        ) : area === 'configuracoes' ? (
          <Administracao secao={secaoDaAdmin} aoTrocarDeSecao={definirSecaoDaAdmin} />
        ) : area === 'etapas-do-funil' ? (
          /*
           * As etapas do funil, no submenu do Funil (28/10): a tela é a seção
           * das Configurações, com o cabeçalho e as ações dela. Um link dela
           * para outra seção leva às Configurações.
           */
          <Administracao
            secao="etapas"
            aoTrocarDeSecao={(secao) => {
              definirSecaoDaAdmin(secao);
              definirArea('configuracoes');
            }}
          />
        ) : area === 'organizacoes' && podeCriarOrganizacao ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <PaginaDeOrganizacoes
              filtro={filtroDasOrganizacoes}
              aoFiltrar={definirFiltroDasOrganizacoes}
              aoAbrir={(id) => {
                definirOrganizacaoAberta(id);
                definirArea('organizacao');
              }}
              aoCriar={() => definirArea('nova-organizacao')}
              aoTrocarDeAba={(parte) => definirArea(parte === 'planos' ? 'planos' : 'organizacoes')}
            />
          </main>
        ) : area === 'organizacao' && organizacaoAberta && podeCriarOrganizacao ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <PaginaDaOrganizacao
              key={organizacaoAberta}
              id={organizacaoAberta}
              aoVoltar={() => definirArea('organizacoes')}
              aoAbrirNovaOrganizacao={() => definirArea('nova-organizacao')}
            />
          </main>
        ) : area === 'nova-organizacao' && podeCriarOrganizacao ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <NovaOrganizacao
              aoVoltar={() => definirArea('organizacoes')}
              aoIrParaPlanos={() => definirArea('planos')}
            />
          </main>
        ) : area === 'planos' && podeCriarOrganizacao ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <PaginaDePlanos
              aoTrocarDeAba={(parte) => definirArea(parte === 'planos' ? 'planos' : 'organizacoes')}
            />
          </main>
        ) : area === 'super-admins' && geraSuperAdmins(me?.papel) ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <TelaDeSuperAdmins />
          </main>
        ) : area === 'feedbacks' && me?.suporte ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <TelaDeFeedbacks />
          </main>
        ) : area === 'identidade' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <RevisaoDeIdentidade />
          </main>
        ) : area === 'clientes' ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <ListaDeClientes
              filtro={filtroDeClientes}
              aoFiltrar={definirFiltroDeClientes}
              aoAbrirCliente={(id) => abrirCliente(id, 'clientes')}
              aoAbrirConversa={abrirConversa}
            />
          </main>
        ) : area === 'cliente' && clienteAberto ? (
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <ClienteTrezentosESessenta
              clienteId={clienteAberto}
              aoAbrirConversa={abrirConversa}
              aoVoltar={() => definirArea(voltarPara)}
            />
          </main>
        ) : (
          /*
           * Atendimento é o PADRÃO, e não mais um ramo entre os outros.
           *
           * O último ramo era uma tela de boas-vindas que confirmava, em voz
           * alta, que "login, contexto e permissões chegaram do servidor" —
           * e mostrava slug, carteira e contagem de caixas numa ficha. Era
           * andaime de desenvolvimento, e era a porta PADRÃO: qualquer área
           * sem tela caía nela.
           *
           * Agora cai em Atendimento, que já é a área inicial. Sem invólucro
           * com respiro: esta tela ocupa a altura inteira e gerencia a própria
           * rolagem por coluna.
           */
          <main className="casca__conteudo casca__conteudo--cheio" id="conteudo">
            <Atendimento
              conversaEscolhida={conversaEscolhida}
              aoEscolher={definirConversaEscolhida}
              aoAbrirCliente={(id) => abrirCliente(id, 'atendimento')}
            />
          </main>
        )}
      </div>

      {/*
        Fora do `<main>`, e por isso em qualquer tela (#536). A conversa
        aberta só conta no atendimento: é o único lugar em que a mensagem
        nova aparece sozinha diante da pessoa.
      */}
      <PilhaDeNotificacoes
        habilitada={papelRecebeNotificacao(me?.papel)}
        conversaAberta={area === 'atendimento' ? conversaEscolhida : null}
        aoAbrirConversa={abrirConversa}
      />

      {/*
        As menções (#569), fora do menu: dentro dele o diálogo ficava preso ao
        grupo do "@" e sumia ao recolher. Ver `mencoes-para-mim.tsx`.
      */}
      {vendoMencoes && (
        <DialogoDeMencoesParaMim
          aoFechar={() => definirVendoMencoes(false)}
          aoAbrirConversa={abrirConversa}
        />
      )}

      {/*
        A confirmação curta depois de salvar ("Contrato salvo."), em qualquer
        tela: montada uma vez, vazia, para o leitor de tela anunciar o texto
        quando ele entra (#765).
      */}
      <AreaDeAvisos />
    </div>
  );
}
